import { Button, Spinner } from '@gmg/gmg-react-components';
import { getCurrentUser } from 'aws-amplify/auth';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { UseMarkUserInvitationAsVisited } from 'src/graphql/useMarkUserInvitationAsVisited';
import { invitationPaths, ROUTES, ROUTE_SEGMENTS } from 'src/routes';
import styled from 'styled-components';
import JoinOrganizationTemplate from './JoinOrganizationTemplate';
import AppContext from 'src/AppContext';

interface JoinPageProps {
    publicRequestEndpoint: string;
}

const ACCEPT_PAGE_PATH = ROUTE_SEGMENTS.acceptedInvitation;
const JOIN_PAGE_PATH = ROUTE_SEGMENTS.join;
export function getJoinOrganizationPagePath(userId: string, targetOrgId: string, targetOrgName: string | undefined) {
    return `${invitationPaths.joinUser(userId)}?id=${targetOrgId}&orgName=${encodeURIComponent(targetOrgName ?? '')}`;
}

export function getOrganizationNameFromSearchParams(searchParams: URLSearchParams): string {
    const orgName = searchParams.get('orgName');
    if (orgName !== null) {
        return orgName;
    }
    // TODO(96664): Remove the legacy base64 'name' fallback once this is released to prod and 30 days (invitation expiry) have passed.
    const legacyEncodedName = searchParams.get('name');
    if (!legacyEncodedName) {
        return '';
    }
    try {
        return atob(legacyEncodedName);
    } catch {
        return '';
    }
}

const JoinOrganizationPage: FunctionComponent<JoinPageProps> = props => {
    const { t } = useTranslation();
    // For the sign up process, it is important to get the userId from the URL of the invitation email.
    // To verify that the user has access to the email.
    const { userId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { featureFlags } = useContext(AppContext);
    const usePartnerPortalDedicatedCognitoClient = featureFlags?.usePartnerPortalDedicatedCognitoClient;
    // With Entra only login, we don't support self sign-up anymore.
    // Entra users will be created via the API from the Manage backend.
    // Entra users will be added to Cognito the first time the user signs in.
    const isSignupSupported = usePartnerPortalDedicatedCognitoClient;

    // If the user is not signed in, we need to get the target organization ID and name from the URL parameters from the invitation email.
    const searchParams = new URLSearchParams(location.search);
    const organizationName = getOrganizationNameFromSearchParams(searchParams);

    const [isSignedIn, setIsSignedIn] = useState<boolean | undefined>(undefined);
    const [isSignedUp, setIsSignedUp] = useState<boolean | undefined>(undefined);

    const redirectTo = `${location.pathname.replace(JOIN_PAGE_PATH, ACCEPT_PAGE_PATH)}${location.search}`;

    const mutation = UseMarkUserInvitationAsVisited({
        apiEndpoint: props.publicRequestEndpoint,
        onSuccess: (data: boolean | undefined, _, __) => {
            setIsSignedUp(data);
        },
        onError: (error: any, _, __) => {
            if (error?.message === 'UserNotFound' && userId) {
                navigate(invitationPaths.joinUserNotInOrg(userId));
            }
        },
    });

    useEffect(() => {
        void (async () => {
            try {
                await getCurrentUser();
                setIsSignedIn(true);
            } catch (_error) {
                setIsSignedIn(false);
            }
        })();
        // This use effect should only run once to determine if the user is signed in or not.
    }, []);

    useEffect(() => {
        if (isSignedIn === false && userId) {
            // Only run invitation query if *not* signed in, *and* userId is defined by the URL params.
            // This mutation leads to an autoconfirmation of the user, so it is important that it is only run when no user is signed in
            // and the userId comes from the URL params of the invitation link (and not from the token).
            mutation.mutate(userId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSignedIn, userId]);

    const showSpinner = (isSignedIn === undefined) || mutation.isPending;

    function getRequiredUrl() {
        if (isSignedIn) {
            return redirectTo;
        }
        // TODO: Remove this once self sign-up is fully deprecated.
        if (isSignupSupported && !isSignedUp) {
            return ROUTES.signup;
        }
        return ROUTES.login;
    }


    return (
        <JoinOrganizationTemplate organizationName={organizationName}>
            {showSpinner
                ? <Spinner />
                : <JoinButtonContainer>
                    <JoinLinkLabel
                        to={getRequiredUrl()}
                        state={{ redirectTo }}
                        aria-label="Join organization link"
                    >
                        <Button variant="primary" size="big" aria-label="Join organization">
                            {t('JoinOrganization.join_btn', 'Join')}
                        </Button>
                    </JoinLinkLabel>
                </JoinButtonContainer>}
        </JoinOrganizationTemplate>
    );
};

const JoinButtonContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    margin: 40px 0 0 0;
`;

const JoinLinkLabel = styled(Link)`
    text-decoration: none;
    /* The full width of the button should be clickable and not only where the label is. */
    min-width: 100px;
`;

export default JoinOrganizationPage;
